/**
 * Data for the sheet editor: the project's sheets (live), the exact template
 * version a sheet pins, and a sheet document with debounced autosave.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { builtinPrintTemplate, parsePrintTemplateSpec, parseSheetDoc, type PrintTemplateSpec, type SheetDoc } from 'shared-types';
import { pusher } from '../../hooks/pusher';
import { useBuiltinTemplateText } from '../print/builtinTemplateText';
import { PrintTemplateSource, useProjectPrintTemplateVersionQuery, useProjectSheetSaveMutation, useProjectSheetsQuery, type ProjectSheetFieldsFragment, type ProjectTemplateLibraryQuery } from '../../types/graphqlTypes';

/** The project's sheets, refetched when someone changes them (`sheets-updated` on the project channel). */
export function useProjectSheets(projectId: string) {
  const query = useProjectSheetsQuery({ variables: { projectId }, skip: !projectId });
  const { refetch } = query;
  useEffect(() => {
    if (!projectId) return;
    const channel = pusher.subscribe(`project-${projectId}`);
    const handler = () => void refetch();
    channel.bind('sheets-updated', handler);
    // The project channel is shared: unbind only.
    return () => {
      channel.unbind('sheets-updated', handler);
    };
  }, [projectId, refetch]);
  return { sheets: query.data?.projectSheets ?? [], loading: query.loading, refetch };
}

export interface SheetTemplate {
  spec: PrintTemplateSpec;
  name: string;
  /** Image assets of an organisation template (for the logo / pictures). */
  assets: { id: string; url: string }[];
}

/**
 * The template a sheet pins: built-ins from shared-types, organisation
 * templates from the library (latest publish) or, for an older pinned
 * version, the stored version itself.
 */
export function useSheetTemplate(projectId: string, sheet: ProjectSheetFieldsFragment | null, library: ProjectTemplateLibraryQuery['projectTemplateLibrary'] | undefined): { template: SheetTemplate | null; loading: boolean } {
  const isOrg = sheet?.templateSource === PrintTemplateSource.Org;
  const libraryTemplate = isOrg ? library?.templates.find((t) => t.id === sheet?.templateId) : undefined;
  const needsVersion = isOrg && (!libraryTemplate || libraryTemplate.version !== sheet?.templateVersion);
  const versionQuery = useProjectPrintTemplateVersionQuery({ variables: { projectId, templateId: sheet?.templateId ?? '', version: sheet?.templateVersion ?? 0 }, skip: !sheet || !needsVersion });
  const builtinText = useBuiltinTemplateText();
  return useMemo(() => {
    if (!sheet) return { template: null, loading: false };
    if (!isOrg) {
      const builtin = builtinPrintTemplate(sheet.templateId);
      return { template: builtin ? { spec: builtin.spec, name: builtinText(builtin).name, assets: [] } : null, loading: false };
    }
    const assets = (libraryTemplate?.assets ?? []).map((a) => ({ id: a.id, url: a.url }));
    const source = needsVersion ? versionQuery.data?.projectPrintTemplateVersion.spec : libraryTemplate?.publishedSpec;
    const parsed = source ? parsePrintTemplateSpec(source) : null;
    const name = needsVersion ? (versionQuery.data?.projectPrintTemplateVersion.name ?? '') : (libraryTemplate?.name ?? '');
    return { template: parsed && 'spec' in parsed ? { spec: parsed.spec, name, assets } : null, loading: needsVersion && versionQuery.loading };
  }, [sheet, isOrg, libraryTemplate, needsVersion, versionQuery.data, versionQuery.loading, builtinText]);
}

export type SheetSaveStatus = 'saved' | 'pending' | 'saving' | 'conflict' | 'error';

const DEBOUNCE_MS = 700;

interface DocState {
  /** Which sheet (+ reload session) this state belongs to. */
  key: string | null;
  doc: SheetDoc | null;
  /** The server's updatedAt of the version this state builds on (optimistic save check). */
  updatedAt: number | null;
  revision: number;
  savedRevision: number;
  status: SheetSaveStatus;
  /** The stored document did not validate (the screen says so instead of waiting for it). */
  unreadable: boolean;
}

const EMPTY_DOC_STATE: DocState = { key: null, doc: null, updatedAt: null, revision: 0, savedRevision: 0, status: 'saved', unreadable: false };

function initialDocState(sheet: ProjectSheetFieldsFragment | null, key: string | null): DocState {
  if (!sheet) return { ...EMPTY_DOC_STATE, key };
  const parsed = parseSheetDoc(sheet.doc);
  if (!('doc' in parsed)) console.error(`Sheet ${sheet.id} does not validate`, parsed.issues);
  return { key, doc: 'doc' in parsed ? parsed.doc : null, updatedAt: sheet.updatedAt, revision: 0, savedRevision: 0, status: 'saved', unreadable: !('doc' in parsed) };
}

/**
 * The sheet's document as the editor changes it, saved a moment after every
 * change. A save that finds someone else's newer version stops with
 * `conflict` (the caller reloads with a new `session`) instead of overwriting.
 */
export function useSheetDocument(projectId: string, sheet: ProjectSheetFieldsFragment | null, session = 0) {
  const [save] = useProjectSheetSaveMutation();
  const key = sheet ? `${sheet.id}#${session}` : null;
  const [state, setState] = useState<DocState>(() => initialDocState(sheet, key));
  // Another sheet (or a reload): start over from the server's document — during render, no effect round-trip.
  if (state.key !== key) setState(initialDocState(sheet, key));
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  }, [state]);

  const update = useCallback((change: (doc: SheetDoc) => SheetDoc) => {
    setState((current) => {
      if (!current.doc) return current;
      const next = change(current.doc);
      return next === current.doc ? current : { ...current, doc: next, revision: current.revision + 1, status: 'pending' };
    });
  }, []);

  const sheetId = sheet?.id ?? null;
  const saveNow = useCallback(async (): Promise<boolean> => {
    const current = latest.current;
    if (!sheetId || !current.doc || current.updatedAt === null || current.key !== key) return false;
    if (current.revision === current.savedRevision) return true;
    const savingRevision = current.revision;
    setState((s) => ({ ...s, status: 'saving' }));
    try {
      const result = await save({ variables: { projectId, sheetId, doc: current.doc, expectedUpdatedAt: current.updatedAt } });
      const savedAt = result.data?.projectSheetSave.updatedAt;
      setState((s) => (s.key !== key ? s : { ...s, updatedAt: savedAt ?? s.updatedAt, savedRevision: savingRevision, status: s.revision === savingRevision ? 'saved' : 'pending' }));
      return true;
    } catch (e) {
      const status: SheetSaveStatus = e instanceof Error && e.message === 'CONFLICT' ? 'conflict' : 'error';
      setState((s) => (s.key !== key ? s : { ...s, status }));
      return false;
    }
  }, [save, projectId, sheetId, key]);

  useEffect(() => {
    if (state.status !== 'pending') return;
    const timer = window.setTimeout(() => void saveNow(), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [state.status, state.revision, saveNow]);

  return { doc: state.doc, unreadable: state.unreadable, update, status: state.status, saveNow };
}
